/**
 * Meal Plans context (supporting): calories and totals (H54).
 *
 * Pure: no I/O. **Shared by the server and the browser** through `surfaces.ts`,
 * so the editor's live totals and the reader's totals are the same arithmetic
 * rather than two copies that drift at the first rounding change.
 *
 * **Calories are computed, never typed**: 4 kcal per gram of protein and of
 * carbohydrate, 9 per gram of fat. Every total also counts the items with no
 * macros, so a sum over half a meal never reads as the whole meal.
 */
import type { Macros, Meal, MealItem, PlanBody, PlanDay } from "./planBody";

export const KCAL_PER_GRAM = { protein: 4, carbs: 4, fat: 9 } as const;

export interface MacroTotals {
  protein: number;
  carbs: number;
  fat: number;
  kcal: number;
  /** Items that count towards this total because they carry macros. */
  counted: number;
  /** Items left out because they carry none. */
  missing: number;
}

export const ZERO_TOTALS: MacroTotals = {
  protein: 0,
  carbs: 0,
  fat: 0,
  kcal: 0,
  counted: 0,
  missing: 0,
};

/** Whole kilocalories for one set of macros. */
export function kcalOf(macros: Macros): number {
  return Math.round(
    macros.protein * KCAL_PER_GRAM.protein +
      macros.carbs * KCAL_PER_GRAM.carbs +
      macros.fat * KCAL_PER_GRAM.fat,
  );
}

/** One decimal, the precision the figures were entered at. */
function tenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function sum(totals: MacroTotals[]): MacroTotals {
  const raw = totals.reduce(
    (acc, t) => ({
      protein: acc.protein + t.protein,
      carbs: acc.carbs + t.carbs,
      fat: acc.fat + t.fat,
      kcal: 0,
      counted: acc.counted + t.counted,
      missing: acc.missing + t.missing,
    }),
    ZERO_TOTALS,
  );
  const grams = {
    protein: tenth(raw.protein),
    carbs: tenth(raw.carbs),
    fat: tenth(raw.fat),
  };
  return { ...raw, ...grams, kcal: kcalOf(grams) };
}

export function itemTotals(item: MealItem): MacroTotals {
  if (!item.macros) return { ...ZERO_TOTALS, missing: 1 };
  return { ...item.macros, kcal: kcalOf(item.macros), counted: 1, missing: 0 };
}

export function mealTotals(meal: Meal): MacroTotals {
  return sum(meal.items.map(itemTotals));
}

export function dayTotals(day: PlanDay): MacroTotals {
  return sum(day.meals.map(mealTotals));
}

/**
 * The plan's average day: every day's total, summed and divided by the number
 * of days. `counted` and `missing` stay the plan-wide counts, which is what a
 * reader needs to know about how complete the average is.
 */
export function averageDay(body: PlanBody): MacroTotals {
  if (body.days.length === 0) return ZERO_TOTALS;
  const total = sum(body.days.map(dayTotals));
  const n = body.days.length;
  const grams = {
    protein: tenth(total.protein / n),
    carbs: tenth(total.carbs / n),
    fat: tenth(total.fat / n),
  };
  return { ...total, ...grams, kcal: kcalOf(grams) };
}

/** True when the plan carries any macros at all, so a summary is worth drawing. */
export function hasMacros(body: PlanBody): boolean {
  return body.days.some((day) =>
    day.meals.some((meal) => meal.items.some((item) => item.macros !== undefined)),
  );
}

/**
 * Each macro's share of the calories, for the split bar. Whole percentages that
 * add up to 100 (the largest remainder takes the rounding), or all zero when
 * there are no calories to split.
 */
export function kcalSplit(totals: Pick<MacroTotals, "protein" | "carbs" | "fat">): {
  protein: number;
  carbs: number;
  fat: number;
} {
  const parts = {
    protein: totals.protein * KCAL_PER_GRAM.protein,
    carbs: totals.carbs * KCAL_PER_GRAM.carbs,
    fat: totals.fat * KCAL_PER_GRAM.fat,
  };
  const all = parts.protein + parts.carbs + parts.fat;
  if (all <= 0) return { protein: 0, carbs: 0, fat: 0 };
  const exact = (["protein", "carbs", "fat"] as const).map((key) => ({
    key,
    value: (parts[key] / all) * 100,
  }));
  const floored = exact.map((e) => ({ ...e, whole: Math.floor(e.value) }));
  let left = 100 - floored.reduce((n, e) => n + e.whole, 0);
  for (const e of [...floored].sort((a, b) => (b.value - b.whole) - (a.value - a.whole))) {
    if (left <= 0) break;
    e.whole += 1;
    left -= 1;
  }
  const out = { protein: 0, carbs: 0, fat: 0 };
  for (const e of floored) out[e.key] = e.whole;
  return out;
}

/** The body as the Consumer gets it when the switch is off: no figures at all. */
export function withoutMacros(body: PlanBody): PlanBody {
  return {
    guidance: body.guidance,
    days: body.days.map((day) => ({
      label: day.label,
      meals: day.meals.map((meal) => ({
        slot: meal.slot,
        items: meal.items.map(({ text, quantity }) => ({ text, quantity })),
      })),
    })),
  };
}
